import * as Sentry from '@sentry/browser';
import * as SentryIntegrations from '@sentry/integrations';
import get from 'lodash.get';
import Env from '../environment';

export default (release = process.env.SENTRY_RELEASE) => {
  const sentryOptions = {
    dsn: process.env.SENTRY_DSN,
    release,
    maxBreadcrumbs: 50,
    attachStacktrace: true,
    environment: Env.name(),
    ignoreErrors: ['Non-Error exception captured'],
  };

  // When we're developing locally
  if (process.env.NODE_ENV !== 'production') {
    // Don't actually send the errors to Sentry
    // sentryOptions.beforeSend = () => null;
    // sentryOptions.ignoreErrors = [];

    // Instead, dump the errors to the console
    sentryOptions.integrations = [
      new SentryIntegrations.Debug({
        // Trigger DevTools debugger instead of using console.log
        debugger: false,
      }),
    ];
  }

  Sentry.init(sentryOptions);

  return {
    Sentry,
    captureException: (err, ctx) => {
      Sentry.configureScope((scope) => {
        if (err.message) {
          // De-duplication currently doesn't work correctly for SSR / browser errors
          // so we force deduplication by error message if it is present
          scope.setFingerprint([err.message]);
        }

        if (err.statusCode) {
          scope.setExtra('statusCode', err.statusCode);
        }

        if (ctx) {
          const { res, errorInfo, query, pathname } = ctx;
          if (res && res.statusCode) {
            scope.setExtra('statusCode', res.statusCode);
          }

          if (typeof window !== 'undefined') {
            scope.setTag('ssr', false);
            scope.setExtra('query', query);
            scope.setExtra('pathname', pathname);
          }

          if (errorInfo) {
            Object.keys(errorInfo).forEach((key) => scope.setExtra(key, errorInfo[key]));
          }
        }
      });

      return Sentry.captureException(err);
    },
    captureResetPasswordError: (err) => {
      Sentry.configureScope((scope) => {
        if (err.message) {
          // De-duplication currently doesn't work correctly for SSR / browser errors
          // so we force deduplication by error message if it is present
          scope.setFingerprint([err.message]);
        }

        // sending more information on
        // Axios errors generated
        // from password reset page
        const responseErrorDesc = get(err, 'response.data.description');
        if (get(err, 'response')) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          // i.e., error.response.status > 200
          if (!responseErrorDesc) {
            // if there is no valid description
            // from error response
            // we log the response headers
            scope.setExtra('error response headers', get(err, 'response.headers'));
          }
        } else if (get(err, 'request')) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          scope.setExtra('error request', get(err, 'request'));
        } else {
          // Something happened in setting up the request that triggered an Error
          Sentry.captureMessage(`[SSO reset password] failed to set up request: ${err.message}`);
        }
        scope.setExtra('error config', get(err, 'config'));

        scope.setExtra('ticket', get(err, 'ticket'));
        scope.setExtra('user', get(err, 'email'));
      });

      return Sentry.captureException(err);
    },
  };
};
